// Remote Support session viewer (support-AGENT side, TeamViewer-style).
// Renders the owner's screen as a live rrweb replay and captures the agent's
// mouse/keyboard over it; every interaction is sent to the owner's client,
// which replays it there as a virtual pointer. The agent never edits local
// state — the owner's browser is the single machine being driven.

const React = require('react');
const { createElement: h, useEffect, useRef, useState } = React;
const { Replayer } = require('rrweb');
require('rrweb/dist/rrweb.css');
require('./RemoteSessionViewer.css');

var MOVE_THROTTLE_MS = 16;   // ~60fps pointer updates to the owner
// Zero playback buffer: rrweb holds an event until its live clock reaches
// that event's timestamp, so any buffer here is pure added lag. At 0 every
// event paints the moment it arrives (late events play immediately), which
// is the lowest latency this design can reach — what's left is the network
// round trip: agent input → owner → owner's DOM change → back.
var LIVE_BUFFER_MS = 0;
// The owner pings through the recorder every ~3s, so a mirror this silent
// means frames were lost. rrweb incrementals build on prior DOM state — one
// gap makes every later frame wrong — so the only fix is a fresh snapshot.
var RESYNC_SILENCE_MS = 10000;

function RemoteSessionViewer(props) {
  var ownerUsername = props.ownerUsername || 'the owner';
  var mirrorBus = props.mirrorBus;
  var onPointer = typeof props.onPointer === 'function' ? props.onPointer : function() {};
  var onLeave = typeof props.onLeave === 'function' ? props.onLeave : function() {};
  var onResync = typeof props.onResync === 'function' ? props.onResync : function() {};

  var stageRef = useRef(null);      // outer measuring box
  var mountRef = useRef(null);      // rrweb root
  var replayerRef = useRef(null);
  var startedRef = useRef(false);
  var lastMoveAtRef = useRef(0);
  var lastEventAtRef = useRef(0);   // watchdog: last mirror frame received
  var lastResyncAtRef = useRef(0);
  var lastErrorAtRef = useRef(0);
  // Owner's viewport size (from rrweb Meta events) — drives the scale.
  var [remoteSize, setRemoteSize] = useState(null);
  var [scale, setScale] = useState(1);
  var [connected, setConnected] = useState(false);

  // Live replay: subscribe to the mirror bus; first event starts the
  // replayer in live mode, everything else is appended as it arrives.
  useEffect(function() {
    if (!mirrorBus || !mountRef.current) return;
    var replayer = new Replayer([], {
      root: mountRef.current,
      liveMode: true,
      mouseTail: false,
      showWarning: false,
      showDebug: false
    });
    replayerRef.current = replayer;

    lastEventAtRef.current = Date.now();
    var unsubscribe = mirrorBus.subscribe(function(event) {
      if (!event || typeof event !== 'object') return;
      lastEventAtRef.current = Date.now();
      // Meta event carries the owner's viewport dimensions.
      if (event.type === 4 && event.data) {
        setRemoteSize({ width: event.data.width, height: event.data.height });
      }
      // Re-stamp with LOCAL receipt time: the socket already delivers events
      // in order, and the replayer's live clock must never wait on the
      // OWNER'S clock — skew, jitter or a mid-stream resync snapshot would
      // schedule events into the future and freeze the mirror.
      var stamped = Object.assign({}, event, { timestamp: Date.now() });
      if (!startedRef.current) {
        startedRef.current = true;
        replayer.startLive(stamped.timestamp - LIVE_BUFFER_MS);
        setConnected(true);
      }
      try { replayer.addEvent(stamped); } catch (err) {
        var now = Date.now();
        if (now - lastErrorAtRef.current > 5000) {
          lastErrorAtRef.current = now;
          console.warn('[remote-support] mirror frame failed to apply:', err);
        }
      }
    });

    // Silence watchdog → ask the owner for a fresh full snapshot. Covers
    // lost frames from socket blips, server restarts and backpressure
    // terminations — without it the mirror stays stale forever.
    var watchdog = setInterval(function() {
      var now = Date.now();
      if (now - lastEventAtRef.current < RESYNC_SILENCE_MS) return;
      if (now - lastResyncAtRef.current < RESYNC_SILENCE_MS) return;
      lastResyncAtRef.current = now;
      onResync();
    }, 3000);

    return function() {
      unsubscribe();
      clearInterval(watchdog);
      try { replayer.pause(); } catch (_) {}
      replayerRef.current = null;
      startedRef.current = false;
    };
  }, [mirrorBus]);

  // Fit the owner's viewport inside the stage, keeping aspect ratio.
  useEffect(function() {
    function fit() {
      if (!stageRef.current || !remoteSize || !remoteSize.width) return;
      var bounds = stageRef.current.getBoundingClientRect();
      var next = Math.min(bounds.width / remoteSize.width, bounds.height / remoteSize.height);
      setScale(next > 0 ? next : 1);
    }
    fit();
    window.addEventListener('resize', fit);
    return function() { window.removeEventListener('resize', fit); };
  }, [remoteSize]);

  // Map stage coords -> the owner's viewport coords.
  function remoteCoords(event) {
    var overlay = event.currentTarget;
    var rect = overlay.getBoundingClientRect();
    return {
      x: Math.round((event.clientX - rect.left) / scale),
      y: Math.round((event.clientY - rect.top) / scale)
    };
  }

  // Buttons + modifiers travel with every event so drags, right-clicks and
  // ctrl/shift-clicks behave the same on the owner's side.
  function pointerMeta(event) {
    return {
      button: event.button,
      buttons: event.buttons,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey
    };
  }

  function handleMove(event) {
    var now = Date.now();
    if (now - lastMoveAtRef.current < MOVE_THROTTLE_MS) return;
    lastMoveAtRef.current = now;
    var pos = remoteCoords(event);
    onPointer(Object.assign({ kind: 'move', x: pos.x, y: pos.y }, pointerMeta(event)));
  }

  function forward(kind) {
    return function(event) {
      var pos = remoteCoords(event);
      onPointer(Object.assign({ kind: kind, x: pos.x, y: pos.y }, pointerMeta(event)));
    };
  }

  function handleWheel(event) {
    event.preventDefault();
    var pos = remoteCoords(event);
    onPointer(Object.assign(
      { kind: 'wheel', x: pos.x, y: pos.y, deltaX: event.deltaX, deltaY: event.deltaY },
      pointerMeta(event)
    ));
  }

  function handleContextMenu(event) {
    event.preventDefault();
    var pos = remoteCoords(event);
    onPointer(Object.assign({ kind: 'contextmenu', x: pos.x, y: pos.y }, pointerMeta(event)));
  }

  // Leaving the stage clears hover state on the owner's side so nothing
  // stays stuck highlighted.
  function handleMouseLeave(event) {
    var pos = remoteCoords(event);
    onPointer(Object.assign({ kind: 'leave', x: pos.x, y: pos.y }, pointerMeta(event)));
  }

  function handleKeyDown(event) {
    event.preventDefault();
    onPointer({
      kind: 'key',
      key: event.key,
      code: event.code,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey
    });
  }

  var frameStyle = remoteSize
    ? {
        width: remoteSize.width * scale + 'px',
        height: remoteSize.height * scale + 'px'
      }
    : null;
  var scaleStyle = remoteSize
    ? {
        width: remoteSize.width + 'px',
        height: remoteSize.height + 'px',
        transform: 'scale(' + scale + ')',
        transformOrigin: 'top left'
      }
    : null;

  return h('div', { className: 'remote-session-viewer' },
    h('div', { className: 'remote-session-topbar' },
      h('div', { className: 'remote-session-title' },
        h('span', { className: 'remote-session-live-dot' }),
        'Remote support — driving ', h('strong', null, ownerUsername), '’s screen'
      ),
      h('div', { className: 'remote-session-hint' }, 'Your mouse and keyboard act on their browser'),
      h('button', { className: 'remote-session-leave', onClick: onLeave }, 'Leave session')
    ),
    h('div', { className: 'remote-session-stage', ref: stageRef },
      !connected
        ? h('div', { className: 'remote-session-waiting' }, 'Connecting to ' + ownerUsername + '’s screen…')
        : null,
      h('div', { className: 'remote-session-frame', style: frameStyle },
        h('div', { className: 'remote-session-scale', style: scaleStyle },
          h('div', { className: 'remote-session-mount', ref: mountRef })
        ),
        // Input capture sits above the replay; tabIndex makes it typeable.
        h('div', {
          className: 'remote-session-overlay',
          tabIndex: 0,
          onMouseMove: handleMove,
          onMouseDown: function(event) { event.currentTarget.focus(); forward('down')(event); },
          onMouseUp: forward('up'),
          onClick: forward('click'),
          onDoubleClick: forward('dblclick'),
          onContextMenu: handleContextMenu,
          onMouseLeave: handleMouseLeave,
          onWheel: handleWheel,
          onKeyDown: handleKeyDown
        })
      )
    )
  );
}

module.exports = RemoteSessionViewer;
